import { AgentAvatar } from "@/components/molecules/agent-avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useMemo } from "react";
import { useTranslation } from "@/features/utils/i18n";

interface AgentNameProps {
  agentId: string;
  fallback?: string;
  showAvatar?: boolean;
  avatarSize?: "sm" | "md" | "lg" | "xl";
}

export function AgentName({
  agentId,
  fallback,
  showAvatar = true,
}: AgentNameProps) {
  const { t } = useTranslation();
  fallback ??= t("common.unknownAgent");
  const { data: agents = [], isLoading } = useAgents();

  const agent = useMemo(
    () => agents.find((a) => a.id === agentId),
    [agents, agentId],
  );

  if (!agentId) {
    return <span className="text-muted-foreground">{fallback}</span>;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        {showAvatar && <Skeleton className="h-4 w-4 rounded-full" />}
        <Skeleton className="h-4 w-24" />
      </div>
    );
  }

  if (!agent) {
    return <span className="text-muted-foreground">{fallback}</span>;
  }

  return (
    <div className="flex items-center gap-2">
      {showAvatar && (
        <AgentAvatar
          className="h-4 w-4"
          avatarId={agent.avatar_id}
          name={agent.name}
          size="lg"
        />
      )}
      <span>{agent.name}</span>
    </div>
  );
}
